import type { StreamingEvent } from '../../types/chat.types';
import type {
  ContentProcessorState,
  ContentProcessorToolUse,
  ToolUseNode,
} from '../contentProcessor.types';

import { WORKFLOW_TOOL_NAME } from '../../types/constants';
import { appendTextNode } from '../nodes';

export function handleToolUsageStartEvent(
  state: ContentProcessorState,
  event: StreamingEvent<'tool_usage_start'>,
): ContentProcessorState {
  // AskUserQuestion: may have been created from progress-before-start; update title only
  const existingFromProgress = state.toolUses[event.data.tool_use_id];
  const existingNode = state.toolUseStack.find(
    (node) => node.id === event.data.tool_use_id,
  );
  if (
    existingFromProgress != null &&
    existingNode != null &&
    event.data.name === 'AskUserQuestion'
  ) {
    const startTitle = event.data.message.trim();
    const resolvedTitle =
      startTitle !== '' && !startTitle.startsWith('User has answered')
        ? startTitle
        : 'Ask User Question';
    existingNode.data.title = resolvedTitle;
    return {
      ...state,
      chunks: {
        ...state.chunks,
        tool_usage_start: [...state.chunks.tool_usage_start, event.data],
      },
      toolUses: {
        ...state.toolUses,
        [event.data.tool_use_id]: {
          ...existingFromProgress,
          start: event.data,
        },
      },
    };
  }

  const newToolUse: ContentProcessorToolUse = {
    start: event.data,
    progress: [],
  };

  // Create a new tool-use node
  // For AskUserQuestion (like Read tool): title from start, result from stop, progress in accordion
  // AskUserQuestion: use "Ask User Question" as title - start message can be wrong (e.g. progress content)
  // when events are replayed or from API; Read tool uses short title from start
  const rawTitle = event.data.message.trim();
  let title: string;
  if (event.data.name === 'AskUserQuestion') {
    title =
      rawTitle && !rawTitle.startsWith('User has answered')
        ? rawTitle
        : 'Ask User Question';
  } else {
    title = rawTitle;
  }

  const toolUseNode: ToolUseNode = {
    type: 'tool-use',
    id: event.data.tool_use_id,
    data: {
      tool_use_id: event.data.tool_use_id,
      name: event.data.name,
      title,
      status: 'pending',
      result: '',
      parent_tool_use_id: event.data.parent_tool_use_id,
      is_error: false,
    },
    children: [],
  };

  // Determine where to add this tool-use node based on parent_tool_use_id
  const currentParent = event.data.parent_tool_use_id
    ? state.toolUseStack.find(
        (node) => node.id === event.data.parent_tool_use_id,
      )
    : null;
  let updatedNodes = state.nodes;

  if (currentParent) {
    // Add as child of the specified parent tool-use
    currentParent.children.push(toolUseNode);
  } else {
    // Add as root-level node
    updatedNodes = [...state.nodes, toolUseNode];
  }

  return {
    ...state,
    nodes: updatedNodes,
    toolUseStack: [...state.toolUseStack, toolUseNode],
    chunks: {
      ...state.chunks,
      tool_usage_start: [...state.chunks.tool_usage_start, event.data],
    },
    toolUses: {
      ...state.toolUses,
      [event.data.tool_use_id]: newToolUse,
    },
  };
}

export function handleToolUsageProgressEvent(
  state: ContentProcessorState,
  event: StreamingEvent<'tool_usage_progress'>,
): ContentProcessorState {
  const existingToolUse = state.toolUses[event.data.tool_use_id];

  // Track in chunks regardless
  const chunks = {
    ...state.chunks,
    tool_usage_progress: [...state.chunks.tool_usage_progress, event.data],
  };

  // AskUserQuestion (like Read): progress can arrive before start when loading from API
  // Create tool use node so we display correctly
  if (
    existingToolUse == null &&
    event.data.name === 'AskUserQuestion' &&
    event.data.message
  ) {
    const syntheticToolUse: ContentProcessorToolUse = {
      start: { ...event.data, message: 'Ask User Question' },
      progress: [event.data],
    };
    const synthNode: ToolUseNode = {
      type: 'tool-use',
      id: event.data.tool_use_id,
      data: {
        tool_use_id: event.data.tool_use_id,
        name: event.data.name,
        title: 'Ask User Question',
        status: 'pending',
        result: '',
        parent_tool_use_id: event.data.parent_tool_use_id ?? null,
        is_error: false,
      },
      children: appendTextNode([], event.data.message),
    };
    const updatedNodes = [...state.nodes, synthNode];
    return {
      ...state,
      nodes: updatedNodes,
      toolUseStack: [...state.toolUseStack, synthNode],
      chunks,
      toolUses: {
        ...state.toolUses,
        [event.data.tool_use_id]: syntheticToolUse,
      },
    };
  }

  // If no existing tool use in state, just track in chunks
  if (existingToolUse == null) {
    return {
      ...state,
      chunks,
    };
  }

  const updatedToolUse: ContentProcessorToolUse = {
    ...existingToolUse,
    progress: [...existingToolUse.progress, event.data],
  };

  // Find the tool-use node in the stack and update it
  const toolUseNode = state.toolUseStack.find(
    (node) => node.id === event.data.tool_use_id,
  );

  if (toolUseNode && event.data.message) {
    // Workflow: progress messages are structured JSON events consumed by the
    // custom WorkflowBox render. Preserve them verbatim on the node data
    // instead of appending raw JSON text to the accordion children.
    if (toolUseNode.data.name === WORKFLOW_TOOL_NAME) {
      const workflowNode: ToolUseNode = {
        ...toolUseNode,
        data: {
          ...toolUseNode.data,
          progressMessages: [
            ...(toolUseNode.data.progressMessages ?? []),
            event.data.message,
          ],
        },
      };

      const workflowStack = state.toolUseStack.map((node) =>
        node.id === event.data.tool_use_id ? workflowNode : node,
      );

      const workflowNodes = state.nodes.map((node) =>
        node.type === 'tool-use' && node.id === event.data.tool_use_id
          ? workflowNode
          : node,
      );

      return {
        ...state,
        nodes: workflowNodes,
        toolUseStack: workflowStack,
        chunks,
        toolUses: {
          ...state.toolUses,
          [event.data.tool_use_id]: updatedToolUse,
        },
      };
    }

    // For AskUserQuestion (like Read tool): progress always goes to accordion (children)
    // Check if this is a nested node (has parent_tool_use_id)
    const isNested = !!toolUseNode.data.parent_tool_use_id;

    if (isNested) {
      // For nested nodes, mutate directly since the reference is shared with parent.children
      toolUseNode.children = appendTextNode(
        toolUseNode.children,
        event.data.message,
      );

      return {
        ...state,
        chunks,
        toolUses: {
          ...state.toolUses,
          [event.data.tool_use_id]: updatedToolUse,
        },
      };
    }

    // For root-level nodes, use immutable updates - always append progress to children
    const updatedNode: ToolUseNode = {
      ...toolUseNode,
      children: appendTextNode(toolUseNode.children, event.data.message),
    };

    // Update the node in toolUseStack
    const toolUseNodeIndex = state.toolUseStack.findIndex(
      (node) => node.id === event.data.tool_use_id,
    );
    const updatedStack = [...state.toolUseStack];
    updatedStack[toolUseNodeIndex] = updatedNode;

    // Update in nodes array - find by ID for reliable matching
    const updatedNodes = state.nodes.map((node) =>
      node.type === 'tool-use' && node.id === event.data.tool_use_id
        ? updatedNode
        : node,
    );

    return {
      ...state,
      nodes: updatedNodes,
      toolUseStack: updatedStack,
      chunks,
      toolUses: {
        ...state.toolUses,
        [event.data.tool_use_id]: updatedToolUse,
      },
    };
  }

  return {
    ...state,
    chunks,
    toolUses: {
      ...state.toolUses,
      [event.data.tool_use_id]: updatedToolUse,
    },
  };
}

export function handleToolUsageStopEvent(
  state: ContentProcessorState,
  event: StreamingEvent<'tool_usage_stop'>,
): ContentProcessorState {
  const existingToolUse = state.toolUses[event.data.tool_use_id];

  // Track in chunks regardless
  const chunks = {
    ...state.chunks,
    tool_usage_stop: [...state.chunks.tool_usage_stop, event.data],
  };

  // If no existing tool use in state, just track in chunks
  if (existingToolUse == null) {
    return {
      ...state,
      chunks,
    };
  }

  const updatedToolUse: ContentProcessorToolUse = {
    ...existingToolUse,
    stop: event.data,
  };

  // Find the tool-use node and update its status, result, and is_error
  const toolUseNode = state.toolUseStack.find(
    (node) => node.id === event.data.tool_use_id,
  );

  // Pop this tool-use from the stack
  const updatedStack = state.toolUseStack.filter(
    (node) => node.id !== event.data.tool_use_id,
  );

  if (toolUseNode) {
    // Check if this is a nested node (has parent_tool_use_id)
    const isNested = !!toolUseNode.data.parent_tool_use_id;

    if (isNested) {
      // For nested nodes, mutate directly since the reference is shared with parent.children
      toolUseNode.data.status = 'completed';
      toolUseNode.data.result = event.data.message.trim();
      toolUseNode.data.is_error = event.data.is_error ?? false;

      return {
        ...state,
        toolUseStack: updatedStack,
        chunks,
        toolUses: {
          ...state.toolUses,
          [event.data.tool_use_id]: updatedToolUse,
        },
      };
    }

    // For root-level nodes, use immutable updates
    const updatedNodes = state.nodes.map((node) =>
      node.type === 'tool-use' && node.id === event.data.tool_use_id
        ? {
            ...toolUseNode,
            data: {
              ...toolUseNode.data,
              status: 'completed' as const,
              result: event.data.message.trim(),
              is_error: event.data.is_error ?? false,
            },
          }
        : node,
    );

    return {
      ...state,
      nodes: updatedNodes,
      toolUseStack: updatedStack,
      chunks,
      toolUses: {
        ...state.toolUses,
        [event.data.tool_use_id]: updatedToolUse,
      },
    };
  }

  return {
    ...state,
    toolUseStack: updatedStack,
    chunks,
    toolUses: {
      ...state.toolUses,
      [event.data.tool_use_id]: updatedToolUse,
    },
  };
}
